import Decimal from 'decimal.js';
import IPAMaths from '../../../lib/IPAMaths';
import { isBrokenedPriceList } from '../../../lib/ipa_gerenciador_utils';
import {
  ApplyNetworkPriceItem,
  ClusterCellSkin,
  ClusterColumn,
  ClusterLimitBreaks,
  ClusterMetrics,
  DraftPrices,
  LineKey,
  NETWORK_CATEGORY_KEYS,
  NetworkCategoryKey,
  NetworkFilterKey,
  NetworkFilterValues,
  NetworkOptionsCascade,
  NetworkPriceCluster,
  NetworkPriceLine,
  NetworkPriceStatus,
  ProductsToBePricedId,
} from './types';

// Prices closer than half a cent are the same price (two-decimal currency).
const PRICE_TOLERANCE = 0.005;

export const isSamePrice = (price: number, otherPrice: number): boolean => Math.abs(price - otherPrice) < PRICE_TOLERANCE;

const CLUSTER_STATUS_SKINS: Record<NetworkPriceStatus, ClusterCellSkin> = {
  ERP: 'gray',
  AUTO: 'blue',
  MANUAL: 'blue',
  EDITADO: 'blue',
  // Like the price manager, only the OTIMIZADO status pill is pink; the input follows AUTO.
  OTIMIZADO: 'blue',
};

export const getLineKey = (line: NetworkPriceLine): LineKey => (line.productFamilyId ? `F:${line.productFamilyId}` : `P:${line.productId}`);

export const getLineFilter = (line: NetworkPriceLine): NetworkFilterValues =>
  line.productFamilyId ? { productFamilyIds: [line.productFamilyId] } : { productIds: [line.productId] };

// Only the active filters go in the body: an empty list would still restrict the lines on the back-end.
export const buildNetworkPricesFilter = (values: NetworkFilterValues): NetworkFilterValues =>
  Object.fromEntries(Object.entries(values).filter(([, filterValues]) => filterValues?.length));

// The client's segments may also list 'productIds', which already has its own chip in the filter bar.
export const isNetworkCategorySegment = <T extends { value: string }>(segment: T): segment is T & { value: NetworkCategoryKey } =>
  (NETWORK_CATEGORY_KEYS as readonly string[]).includes(segment.value);

// Only these filters cascade; brand and supplier neither narrow nor are narrowed (same as the price manager).
const CASCADE_PARAM_BY_FILTER_KEY: Partial<Record<NetworkFilterKey, keyof NetworkOptionsCascade>> = {
  productIds: 'productId',
  productFamilyIds: 'productFamilyId',
  ...(Object.fromEntries(NETWORK_CATEGORY_KEYS.map(key => [key, key])) as Record<NetworkCategoryKey, NetworkCategoryKey>),
};

// Selections of the other cascading chips, named as the option endpoints expect them.
export const getNetworkCascadingParams = (values: NetworkFilterValues, currentKey: NetworkFilterKey): NetworkOptionsCascade => {
  if (!CASCADE_PARAM_BY_FILTER_KEY[currentKey]) return {};

  return (Object.entries(values) as [NetworkFilterKey, string[] | undefined][]).reduce<NetworkOptionsCascade>((params, [key, filterValues]) => {
    const param = CASCADE_PARAM_BY_FILTER_KEY[key];
    if (key === currentKey || !param || !filterValues?.length) return params;
    return { ...params, [param]: filterValues };
  }, {});
};

// Stable key for a cascade, whatever the order the selections were made in.
export const getNetworkCascadeSignature = (cascade: NetworkOptionsCascade): string =>
  Object.entries(cascade)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, values]) => `${key}:${values.join('|')}`)
    .join(';');

// Union of every line's clusters in first-seen order: a line missing a cluster must not hide that column for the page.
export const getClusterColumns = (lines: NetworkPriceLine[]): ClusterColumn[] => {
  const columns = new Map<string, ClusterColumn>();
  lines.forEach(line =>
    line.clusters.forEach(({ clusterId, clusterName, clusterStoresCount }) => {
      if (!columns.has(clusterId)) columns.set(clusterId, { clusterId, clusterName, clusterStoresCount });
    }),
  );
  return Array.from(columns.values());
};

export const findLineCluster = (line: NetworkPriceLine, clusterId: string): NetworkPriceCluster | undefined =>
  line.clusters.find(cluster => cluster.clusterId === clusterId);

export const formatClusterLabel = (name: string, storesCount: number | null): string => {
  if (storesCount === null) return name;
  return `${name} · ${storesCount} ${storesCount === 1 ? 'loja' : 'lojas'}`;
};

export const isClusterAvailable = (cluster: NetworkPriceCluster): boolean => cluster.productsToBePricedId !== null;

// Same rule as the price manager price cell (no broken-limit filter here): red when the displayed price,
// draft included, breaks a limit. CPI and margin have no input here, so their breaks also paint the price.
// The back-end brokenLimit refers to the automatic suggestion.
export const getClusterCellSkin = (cluster: NetworkPriceCluster, draftPrice: number | null | undefined): ClusterCellSkin => {
  const { priceList, cpi, margin } = getClusterLimitBreaks(cluster, draftPrice);
  if (priceList || cpi || margin) return 'red';
  if (draftPrice !== undefined) return 'blue';
  return (cluster.priceStatus && CLUSTER_STATUS_SKINS[cluster.priceStatus]) || 'gray';
};

const hasLimit = (limit: number | null | undefined): limit is number => limit !== null && limit !== undefined;

// Limits come as fractions (0.28) and CPI/margin as percentages (28).
const toPercentageLimit = (limit: number): number => new Decimal(limit).times(100).toNumber();

// Same rule as the price manager margin cell (isMarginOutsideLimit): either bound alone is a limit.
const isMarginOutsideLimit = (margin: number | null, { minMargin, maxMargin }: NetworkPriceCluster): boolean => {
  if (margin === null) return false;
  if (hasLimit(maxMargin) && margin > toPercentageLimit(maxMargin)) return true;
  return hasLimit(minMargin) && margin < toPercentageLimit(minMargin);
};

// Same rule as the price manager CPI cell (isCpiOutsideLimit): needs a competitor price and both bounds.
const isCpiOutsideLimit = (cpi: number | null, { competitorsPrice, minCpi, maxCpi }: NetworkPriceCluster): boolean => {
  if (!competitorsPrice || competitorsPrice <= 0 || !cpi || !minCpi || !maxCpi) return false;
  return cpi > toPercentageLimit(maxCpi) || cpi < toPercentageLimit(minCpi);
};

export const getClusterLimitBreaks = (cluster: NetworkPriceCluster, draftPrice: number | null | undefined): ClusterLimitBreaks => {
  const price = draftPrice === undefined ? cluster.price : draftPrice;
  const { cpi, margin } = getClusterMetrics(cluster, draftPrice ?? undefined);

  return {
    priceList: isBrokenedPriceList({ price, minPriceList: cluster.minPriceList ?? null, maxPriceList: cluster.maxPriceList ?? null }),
    cpi: isCpiOutsideLimit(cpi, cluster),
    margin: isMarginOutsideLimit(margin, cluster),
  };
};

const hasMarginInputs = ({ salesTaxPercentage, expensesPercent, cost }: NetworkPriceCluster): boolean =>
  salesTaxPercentage !== undefined && expensesPercent !== undefined && cost !== undefined;

const getDraftMargin = (cluster: NetworkPriceCluster, price: number): number | null => {
  if (!hasMarginInputs(cluster)) return null;
  return IPAMaths.novaMargem(price, cluster.salesTaxPercentage ?? null, cluster.expensesPercent ?? null, cluster.cost ?? null);
};

// IPAMaths.novaCompetitividade returns 0 without a competitor price; on this screen that means no CPI at all.
const calcClusterCpi = (price: number | null, competitorsPrice: number | null): number | null =>
  price === null || !competitorsPrice ? null : IPAMaths.novaCompetitividade(price, competitorsPrice);

// Same rule as the price manager (PrecoSugeridoCell): computed on the front, no variation without price or retail price.
const calcClusterPriceVariation = (price: number | null, retailPrice: number | null): number | null =>
  !price || !retailPrice ? null : IPAMaths.variation.price(price, retailPrice);

export const getClusterMetrics = (cluster: NetworkPriceCluster, draftPrice: number | undefined): ClusterMetrics => {
  const price = draftPrice ?? cluster.price;
  const retailCpi = calcClusterCpi(cluster.retailPrice, cluster.competitorsPrice);
  const cpi = draftPrice === undefined ? cluster.cpi : calcClusterCpi(draftPrice, cluster.competitorsPrice);
  const margin = draftPrice === undefined ? cluster.newMargin : getDraftMargin(cluster, draftPrice);

  return {
    price,
    priceVariation: calcClusterPriceVariation(price, cluster.retailPrice),
    cpi,
    cpiVariation: IPAMaths.variation.cpi(cpi, retailCpi),
    margin,
    marginVariation: IPAMaths.variation.margem(margin, cluster.originalMargin),
  };
};

// Weighted by the stores of each cluster carrying the product: a store-by-store average, not an average of the columns.
const calcStoresWeightedAverage = (
  line: NetworkPriceLine,
  getPrice: (cluster: NetworkPriceCluster, cellId: ProductsToBePricedId) => number | null,
): number | null => {
  const totals = line.clusters.reduce(
    (acc, cluster) => {
      const { productsToBePricedId, storesCount } = cluster;
      if (productsToBePricedId === null || storesCount === null || storesCount <= 0) return acc;
      const price = getPrice(cluster, productsToBePricedId);
      if (price === null) return acc;

      return {
        weightedPrice: acc.weightedPrice.plus(new Decimal(price).times(storesCount)),
        storesCount: acc.storesCount + storesCount,
      };
    },
    { weightedPrice: new Decimal(0), storesCount: 0 },
  );

  return totals.storesCount > 0 ? totals.weightedPrice.div(totals.storesCount).toDecimalPlaces(2).toNumber() : null;
};

// The cluster as the back-end confirms a manual price: EDITADO, with its metrics recalculated on the front.
export const confirmClusterPrice = (cluster: NetworkPriceCluster, price: number): NetworkPriceCluster => {
  const metrics = getClusterMetrics(cluster, price);

  return {
    ...cluster,
    price,
    editedPrice: price,
    priceStatus: 'EDITADO',
    priceVariation: metrics.priceVariation,
    cpi: metrics.cpi,
    newMargin: metrics.margin,
  };
};

export const calcNetworkAveragePrice = (line: NetworkPriceLine, draftPrices: DraftPrices): number | null =>
  calcStoresWeightedAverage(line, (cluster, cellId) => draftPrices[cellId] ?? cluster.price);

export const calcNetworkRetailAveragePrice = (line: NetworkPriceLine): number | null => calcStoresWeightedAverage(line, cluster => cluster.retailPrice);

// "SEM VAR." depends only on the two averages being the same price, never on the network status.
export const calcNetworkPriceVariation = (averagePrice: number | null, retailAveragePrice: number | null): number | null => {
  if (averagePrice === null || retailAveragePrice === null) return null;
  if (isSamePrice(averagePrice, retailAveragePrice)) return 0;
  return IPAMaths.variation.price(averagePrice, retailAveragePrice);
};

// Network badge priority, unlike the per-cell hierarchy; MANUAL counts as AUTO.
const NETWORK_STATUS_PRIORITY: NetworkPriceStatus[] = ['EDITADO', 'OTIMIZADO', 'AUTO', 'ERP'];
const NETWORK_STATUS_ALIASES: Partial<Record<NetworkPriceStatus, NetworkPriceStatus>> = { MANUAL: 'AUTO' };

export const getNetworkPriceStatus = (line: NetworkPriceLine): NetworkPriceStatus | null => {
  const statuses = new Set(
    line.clusters.flatMap(({ productsToBePricedId, priceStatus }) =>
      productsToBePricedId === null || priceStatus === null ? [] : [NETWORK_STATUS_ALIASES[priceStatus] ?? priceStatus],
    ),
  );
  return NETWORK_STATUS_PRIORITY.find(status => statuses.has(status)) ?? null;
};

// The network field never shows the broken-limit red: that stays on the affected cluster cells.
export const getNetworkCellSkin = (priceStatus: NetworkPriceStatus | null, hasDraft: boolean): ClusterCellSkin => {
  if (hasDraft) return 'blue';
  return (priceStatus && CLUSTER_STATUS_SKINS[priceStatus]) || 'gray';
};

export const getLineCellIds = (line: NetworkPriceLine): ProductsToBePricedId[] =>
  line.clusters.flatMap(({ productsToBePricedId }) => (productsToBePricedId === null ? [] : [productsToBePricedId]));

export const buildApplyPayload = (lines: NetworkPriceLine[]): ApplyNetworkPriceItem[] =>
  lines.flatMap(line =>
    line.clusters.flatMap(({ productsToBePricedId, price }) => {
      if (productsToBePricedId === null || price === null) return [];
      return [{ productsToBePricedId, price }];
    }),
  );

export const SELECT_COLUMN_WIDTH = 44;
export const PRODUCT_COLUMN_MIN_WIDTH = 260;
export const CLUSTER_COLUMN_WIDTH = 150;
export const AVERAGE_COLUMN_WIDTH = 150;
// rsuite keeps a scrollbar-width gutter on the left of the fixed right column.
const SCROLL_GUTTER_WIDTH = 10;

export type ScrollRange = { start: number; end: number };

// Width of the horizontally scrollable area: the product column takes the leftover space, down to its minimum.
export const getScrollViewportWidth = (tableWidth: number, scrollContentWidth: number): number => {
  const productWidth = Math.max(PRODUCT_COLUMN_MIN_WIDTH, tableWidth - SELECT_COLUMN_WIDTH - AVERAGE_COLUMN_WIDTH - scrollContentWidth);
  return Math.max(0, tableWidth - SELECT_COLUMN_WIDTH - productWidth - AVERAGE_COLUMN_WIDTH - SCROLL_GUTTER_WIDTH);
};

// Smallest scroll change that brings the whole range into view, never pushing its start out of view.
export const getScrollLeftToReveal = (scrollLeft: number, { start, end }: ScrollRange, viewportWidth: number): number => {
  if (start < scrollLeft) return start;
  if (end > scrollLeft + viewportWidth) return Math.min(start, end - viewportWidth);
  return scrollLeft;
};
